"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { MESSAGE_COUNTER_THRESHOLD } from "@/lib/validators/contact-form";

interface ContactCharacterCounterProps {
  /** Current length of the field's value. */
  length: number;
  /** The cap the schema enforces. */
  max: number;
}

/**
 * How much room is left, once there is little enough left to care about.
 *
 * Announcement is deliberately not per keystroke. An `aria-live` region that
 * updates on every character turns typing into a stream of interruptions, so
 * the visible number is hidden from assistive tech and a separate region speaks
 * only when a remaining-count threshold is crossed — and again once the value
 * goes over, which is the one moment the visitor must hear about.
 */
const ANNOUNCE_AT = [0, 20, 50, 100, 200] as const;

/** Sentinel for "over the cap", which is not a remaining-count threshold. */
const OVER = -1;

export function ContactCharacterCounter({
  length,
  max,
}: ContactCharacterCounterProps) {
  const t = useTranslations("contactForm");
  const remaining = max - length;
  const isOver = remaining < 0;

  const [announcement, setAnnouncement] = useState("");
  const lastAnnounced = useRef<number | null>(null);

  const label = isOver
    ? t("charactersOver", { count: -remaining })
    : t("charactersLeft", { count: remaining });

  useEffect(() => {
    // Ascending, so `find` returns the *tightest* interval the count has
    // reached. Descending order silently pins the threshold at the loosest one
    // and the announcement then never updates again — the countdown goes quiet
    // exactly as it starts to matter.
    const threshold = isOver
      ? OVER
      : (ANNOUNCE_AT.find((value) => remaining <= value) ?? null);

    // Back above every threshold — re-arm, so trimming and re-typing past one
    // announces again.
    if (threshold === null) {
      lastAnnounced.current = null;
      setAnnouncement("");
      return;
    }

    if (lastAnnounced.current === threshold) return;
    lastAnnounced.current = threshold;
    setAnnouncement(label);
  }, [remaining, isOver, label]);

  if (remaining > MESSAGE_COUNTER_THRESHOLD) return null;

  return (
    <>
      <span
        aria-hidden="true"
        className={cn(
          "shrink-0 text-xs tabular-nums",
          isOver ? "font-medium text-red-400" : "text-site-foreground/60",
        )}
      >
        {label}
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </>
  );
}
